import {
  Alert,
  Autocomplete,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Group,
  Modal,
  OptionsFilter,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useListState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { logError } from "api/diagnostic.service";
import CustomPageNotification from "components/layouts/CustomPageNotification";
import DefaultButtonAdd from "pages/admin/components/DefaultButtonAdd";
import { SavingModal } from "pages/modals";
import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";
import { IDataEntryFormProps } from "types/interface.types";
import {
  AdminOrderEntries,
  AdminOrderEntry,
  DebtorSummary,
  OrderDataEntryForm,
} from "types/model.types";

import {
  IconAlertCircle,
  IconCheck,
  IconDeviceFloppy,
  IconPrinter,
  IconProgressCheck,
} from "@tabler/icons-react";

import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_INVALID,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
  NAVIGATE_ORDER,
} from "common/constants";
import {
  formatNZD,
  formatPercent,
  getGstAmount,
  getGstRateAsDecimal,
  getLocalDateString,
  safeNumber,
} from "common/utils";
import { useProgressiveDebtorFetch } from "hooks/useProgressiveDebtorFetch";

import { Select, Text } from "@mantine/core";
import { safeApiCall } from "api/common.service";
import {
  changeOrderDate,
  getAdminOrderById,
  submitAdminOrder,
  updateAdminOrderPrintState,
} from "api/order.service";
import { getStaffUsers } from "api/user.service";
import { navigateTo } from "common/navigation";
import SafeHtml from "components/SafeHtml";
import { useProductStore } from "stores/useProductStore";
import { OrderEntryRow, OrderEntryRowHandle } from "./AdminOrderEntryRow";
import { OrderEntryHeaderRow } from "./DataEntryHeaderDefinitions";

const AdminOrderEntryPage: React.FC<IDataEntryFormProps> = ({
  recordId,
  onSaveComplete,
}) => {
  const rowRefs = useRef<Record<number, OrderEntryRowHandle | null>>({});
  //const apiFetchRef = useRef<boolean>(false);
  const [printOrderStage, setPrintOrderStage] = useState<number>(0);
  const [systemNotes, setSystemNotes] = useState<string | null>(null);
  const [showSystemNotes, setShowSystemNotes] = useState<boolean>(false);
  const allProducts = useProductStore((s) => s.allProducts);
  const [triggerReload, setTriggerReload] = useState(new Date()); // used to trigger reload of product data after adding new product
  const [invoiceToInputValue, setInvoiceToInputValue] = useState("");
  const [deliverToInputValue, setDeliverToInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [salesPeople, setSalesPeople] = useState<
    { value: string; label: string }[]
  >([]);
  const [overDue, setOverdue] = useState<boolean>(false);
  const [selectedDebtor, setSelectedDebtor] = useState<DebtorSummary | null>(
    null,
  );
  const [selectedDeliverToDebtor, setSelectedDeliverToDebtor] =
    useState<DebtorSummary | null>(null);
  const invoiceToRef = useRef<HTMLInputElement>(null);
  const deliverToRef = useRef<HTMLInputElement>(null);
  const [activeCustomer, setActiveCustomer] = useState<boolean>(true);
  const [entries, handlers] = useListState<AdminOrderEntry>([]);
  const [invoicePrinted, setInvoicePrinted] = useState(false);
  const [packingSlipPrinted, setPackingSlipPrinted] = useState(false);
  const {
    debtors,
    loading: debtorLoading,
    error: debtorError,
  } = useProgressiveDebtorFetch();
  const [fetchingInvoices, setFetchingInvoices] = useState(false);
  const [saving, setSaving] = React.useState(false);
  const [initialData, setInitialData] = useState<OrderDataEntryForm | null>(
    null,
  );
  const [orderDate, setOrderDate] = useState<string | null>(
    new Date().toUTCString(),
  );
  const debtorOptions: ComboboxItem[] = debtors
    .filter((d) => d.active === true)
    .map((debtor) => ({
      value: `${debtor.id}`,
      label: `${debtor.name} [${debtor.id}]`,
    }));
  const optionsFilter: OptionsFilter = ({ options, search }) => {
    const searchWords = search.toLowerCase().trim().split(" ");

    return (options as ComboboxItem[]).filter((option) => {
      const labelWords = option.label?.toLowerCase().trim().split(" ") ?? [];
      return searchWords.every((word) =>
        labelWords.some((labelWord) => labelWord.includes(word)),
      );
    });
  };
  function formatLabel(email: string): string {
    const namePart = email.split("@")[0];
    return namePart.replace(/\./g, " ");
  }

  const resolveDebtorLabel = (id: number): string => {
    const l = debtors.length;
    return debtorOptions.find((d) => Number(d.value) === id)?.label ?? "";
  };

  // Define our useForm and must be declared before any UseEffects
  const form = useForm<OrderDataEntryForm>({
    initialValues: initialData ?? {
      id: 0,
      summary: {
        id: 0,
        invoiceId: 0,
        createdOn: getLocalDateString(),
        orderStage: 0,
        invoiceToDebtorId: 0,
        invoiceToDebtorName: "",
        deliverToDebtorId: 0,
        deliverToDebtorName: "",
        yourOrderNo: "",
        notes: "",
        notes2: "",
        salesPerson: "",
        isPickingListPrinted: false,
        isPackingSlipPrinted: false,
        isInvoicePrinted: false,
        isProcessed: false,
        totalAmount: 0,
        totalGst: 0,
      },
      entries: [],
    },
  });
  const { summary } = form.getValues();

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      const users = await safeApiCall(getStaffUsers());
      const mapped = users
        .map((u: { userName: string }) => ({
          value: u.userName,
          label: formatLabel(u.userName),
        }))
        .sort(
          (
            a: { value: string; label: string },
            b: { value: string; label: string },
          ) => a.label.localeCompare(b.label),
        ); // sort by label
      if (!mounted) return;

      setSalesPeople(mapped);
    };

    fetchUsers();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (debtors.length === 0 || !recordId) return;

    let mounted = true;
    const fetchInitialData = async () => {
      try {
        const response = await safeApiCall(getAdminOrderById(recordId));
        if (!mounted) return;

        if (response) {
          let notes = null;

          // invoiceTo and deliverTo are always the same
          if (response.summary.invoiceToDebtorId) {
            const label = resolveDebtorLabel(
              response.summary.invoiceToDebtorId,
            );
            // get debtor discount
            const debtor = debtors.find(
              (d) => d.id === response.summary.invoiceToDebtorId,
            );
            setSelectedDebtor(debtor ?? null);
            setSelectedDeliverToDebtor(debtor ?? null);

            if (debtor && debtor.active === false) {
              setActiveCustomer(false);
            }
            setOverdue(debtor?.overdue ?? false);
            notes = debtor?.specialNotes ?? null;

            setInvoiceToInputValue(label);
            setDeliverToInputValue(label);
          }

          setSystemNotes(notes);
          const created = response.summary.createdOn;

          if (created) {
            const iso = response.summary.createdOn; // "2026-05-27T00:00:00Z"
            const dateOnly = iso.split("T")[0]; // "2026-05-27"

            const [y, m, d] = dateOnly.split("-").map(Number);
            const localDate = new Date(); //y, m - 1, d);
            localDate.setFullYear(y, m - 1, d);
            localDate.setHours(0, 0, 0, 0);
            setOrderDate(localDate.toUTCString());
          } else {
            setOrderDate(new Date().toUTCString());
          }

          const updatedResponse = { ...response, entries: [] };
          startTransition(() => {
            form.setValues(updatedResponse);
            handlers.setState(response.entries);
          });
        }
      } catch (error) {
        logError(error);
      } finally {
        //
      }
    };
    fetchInitialData();

    return () => {
      mounted = false;
    };
  }, [recordId, debtors, triggerReload]);
  const invalid = entries.some((e) => e.supplied > e.ordered);
  const { total, gst } = useMemo(() => {
    const total = entries.reduce((sum, entry) => {
      const supplied = entry.supplied || 0;
      const unitPrice = entry.unitPrice || 0;
      const discount = entry.discount || 0;

      // line total = supplied × unitPrice − discount
      //const lineTotal = supplied * (unitPrice ? Number(unitPrice) : 0) * (1 - discount);
      const lineTotal = safeNumber(
        supplied * Number(unitPrice) * (1 - discount),
        0,
        2,
      );

      return sum + lineTotal;
    }, 0);

    const rounded = safeNumber(Math.round(total * 100) / 100, 0, 2);
    const gst = getGstAmount(rounded); // GST = 15% of total

    return { total: rounded, gst };
  }, [entries]);

  const handleRemoveEntry = (index: number) => {
    if (summary.isProcessed) return;

    const entry = entries[index];

    // If entry has no DB ID → remove immediately
    if (!entry.id) {
      handlers.remove(index);
      return;
    }

    // Toggle softDelete
    const newSoftDelete = !entry.softDelete;

    handlers.setItem(index, {
      ...entry,
      softDelete: newSoftDelete,
    });
  };

  const handleUpdateEntry = (
    index: number,
    updated: AdminOrderEntry,
    forceRecalc = false,
  ) => {
    if (summary.isProcessed) return; // should never happen as the remove buttons are disabled when processed, but just in case...
    // Lookup product only if productId has changed
    const product = allProducts.items.find(
      (p) => Number(p.productCode) === updated.productId,
    );

    if (product) {
      // sell price depends on debtor level
      let eachPrice = product.sellPrice1;
      if (selectedDebtor?.sellPriceLevel) {
        switch (selectedDebtor?.sellPriceLevel) {
          case 2: {
            eachPrice = product.sellPrice2 ?? 0;
            break;
          }
          case 3: {
            eachPrice = product.sellPrice3 ?? 0;
            break;
          }
          case 4: {
            eachPrice = product.sellPrice4 ?? 0;
            break;
          }
          default: {
            eachPrice = product.sellPrice1 ?? 0;
            break;
          }
        }
      }

      //let newUnitPrice = eachPrice; // default
      let newUnitPrice: number =
        updated.productCode === "E"
          ? eachPrice
          : safeNumber(eachPrice * (product.unitsCtn ?? 1));

      const raw = updated.unitPrice;
      // Normalize: treat empty string as null
      const updatedUnitPrice =
        raw === null || raw === undefined ? null : Number(raw);

      if (
        forceRecalc ||
        updatedUnitPrice === null ||
        Number.isNaN(updatedUnitPrice)
      ) {
        // No user value → derive from eachPrice
        newUnitPrice =
          updated.productCode == "E"
            ? safeNumber(eachPrice)
            : safeNumber(eachPrice * (product.unitsCtn ?? 1));
      } else {
        newUnitPrice = safeNumber(updatedUnitPrice);
      }
      const newCartonPrice =
        updated.productCode === "C"
          ? newUnitPrice
          : newUnitPrice * (product.unitsCtn ?? 1);

      const newEachPrice =
        updated.productCode === "E"
          ? newUnitPrice
          : safeNumber(newUnitPrice / (product.unitsCtn ?? 1));

      // const total =
      //   updated.supplied * Number(newUnitPrice) * (1 - (updated.discount || 0));
      // const gstAmount = total * getGstRateAsDecimal();

      const rawTotal =
        updated.supplied * Number(newUnitPrice) * (1 - (updated.discount || 0));
      const total = safeNumber(rawTotal, 0, 2); // round per line
      const rawGst = total * getGstRateAsDecimal();
      const gstAmount = safeNumber(rawGst, 0, 2); // round per line

      let productTitle = product.title;
      if (productTitle.indexOf("CTN") < 0) {
        productTitle += ` (${product.unitsCtn} CTN)`;
      }
      handlers.setItem(index, {
        ...updated,
        description: productTitle,
        unitPrice: newUnitPrice,
        eachPrice: newEachPrice,
        cartonPrice: newCartonPrice,
        discount: updated.discount,
        total: total,
        gst: gstAmount,
      });
    } else {
      handlers.setItem(index, {
        ...updated,
        description: "Unknown Product",
      });
    }
  };

  // mark as printed
  const handlePrintPickingList = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    try {
      handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout
      setPrintOrderStage(0);
      const { summary } = form.getValues();

      await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          true,
          summary.isPackingSlipPrinted,
          summary.isInvoicePrinted,
          summary.invoiceId,
        )
          .then((resp) => {
            if (form.getValues().summary.invoiceId === 0)
              form.setFieldValue("summary.invoiceId", resp.invoiceId);

            form.setFieldValue("summary.isPickingListPrinted", true);

            const id = recordId || summary.id;
            setTimeout(() => {
              navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${0}&e=1`);
            }, 500);
          })
          .catch((err) => {
            notifications.show({
              title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
              message: MSG_DEFAULT_FORM_SUBMIT_ERR,
              position: MantineNotificationPosition.TopCenter,
              color: MantineNotificationColor.Error,
            });
          }),
      );
    } catch (err) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      // log but ignore
      logError(err);
    }
  };
  // mark as printed
  // packing slip can only be printed after picking list is printed
  const handlePrintPackingSlip = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }
    try {
      handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout
      setPrintOrderStage(1);
      const { summary } = form.getValues();
      const response = await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          true,
          true,
          summary.isInvoicePrinted,
          summary.invoiceId,
        ),
      );

      if (response && response.invoiceId) {
        if (form.getValues().summary.invoiceId === 0)
          form.setFieldValue("summary.invoiceId", response.invoiceId);
      }

      setPackingSlipPrinted(true);

      form.setFieldValue("summary.orderStage", 1);
      form.setFieldValue("summary.isPickingListPrinted", true);
      form.setFieldValue("summary.isPackingSlipPrinted", true);

      const id = recordId || summary.id;
      setTimeout(() => {
        navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${1}&e=1`);
      }, 500);
    } catch (err) {
      // log but ignore
      logError(err);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  const handlePrintInvoice = async () => {
    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    //  mark as printed
    // exception.. we need to set invoice has been printed
    handleSubmit(false, false, true); // save first to ensure we have an order ID for the printout

    setPrintOrderStage(2);
    const { summary } = form.getValues();

    try {
      await safeApiCall(
        updateAdminOrderPrintState(
          Number(summary.id),
          summary.isPickingListPrinted,
          summary.isPackingSlipPrinted,
          true,
          summary.invoiceId,
        ),
      );
      setInvoicePrinted(true);

      form.setFieldValue("summary.isInvoicePrinted", true);
      const id = recordId || summary.id;
      setTimeout(() => {
        navigateTo(`${NAVIGATE_ORDER}?id=${id}&s=${2}&e=1`);
      }, 500);
    } catch (err) {
      logError(err);
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_SUBMIT_ERR,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };

  // determine if we can process this order
  const canProcess = (): boolean => {
    const { summary } = form.getValues();

    if (summary.isProcessed) {
      return false; // negate - already processed
    }

    // Otherwise, allow processing if not all slips are printed,
    // or if customer is inactive, or if saving
    const allPrinted =
      summary.isPackingSlipPrinted &&
      summary.isInvoicePrinted &&
      summary.isPickingListPrinted;
    return allPrinted && activeCustomer; // all printed and active customer
  };
  /**
   * Process the credit note.  This works by setting the isProcessed flag to true
   * and then calling handleSubmit to save the form.  The backend will handle the rest.
   * @returns
   */
  const handleProcess = async () => {
    // Let React commit state before submitting
    setTimeout(() => {
      handleSubmit(true, true);
    }, 0);
  };
  function stripId(name: string): string {
    if (!name) return "";
    // Matches " [digits]" at the end inside square brackets
    return name.replace(/\s*\[\d+\]$/, "").trim();
  }

  /**
   * Save the form data to the backend.
   * @param close If true, close the form after saving
   * @returns
   */
  const handleSubmit = async (
    close = false,
    process = false,
    silent = false,
  ) => {
    const formData: OrderDataEntryForm = form.getValues();
    if (formData.summary.isProcessed) return; // already processed...

    if (
      form.validate().hasErrors ||
      invoiceToInputValue === "" ||
      deliverToInputValue === "" ||
      formData.summary.salesPerson === "" ||
      (process && canProcess() === false)
    ) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: MSG_DEFAULT_FORM_INVALID,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    if (invoiceToInputValue !== deliverToInputValue) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "Invoice To and Deliver To doesn't match.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    if (invalid) {
      notifications.show({
        title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
        message: "One or more items have supplied greater than ordered.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      return;
    }

    // form is invalid.
    setSaving(true);

    try {
      //On save, you must force a recalculation for every entry, not just the ones the user touched.
      entries.forEach((entry, index) => {
        handleUpdateEntry(index, entry, true);
      });

      // inject the input values into the payl oad
      const payload: OrderDataEntryForm = {
        ...formData,
        summary: {
          ...formData.summary,
          invoiceToDebtorName: stripId(invoiceToInputValue),
          deliverToDebtorName: stripId(deliverToInputValue),
          isProcessed: process,
        },
        entries,
      };

      const result = await safeApiCall(submitAdminOrder(payload));
      if (result?.error) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: result?.error || "An unknown error occurred while saving.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
        return;
      }
      // Merge backend response into form
      if (!result?.entries || !Array.isArray(result.entries)) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: "Invalid response: entries is missing or not an array.",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });

        return;
      }

      const updatedEntries: AdminOrderEntries = result.entries
        .map((updatedEntry: AdminOrderEntry) => {
          const existing = entries?.find(
            (e) => e.productId === updatedEntry.productId,
          );

          return {
            ...existing,
            ...updatedEntry, // this includes the new `id`
          };
        })
        .sort(
          (a: AdminOrderEntry, b: AdminOrderEntry) => a.productId - b.productId,
        );

      startTransition(() => {
        form.setValues({
          summary: {
            ...formData.summary,
            id: result.orderId,
          },
        });
        handlers.setState(updatedEntries);
      });

      if (!silent) {
        notifications.show({
          title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
          message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });
      }

      (process || close) && onSaveComplete(close);
    } catch (error) {
      notifications.show({
        title: "Journal Entry",
        message: `Failed to submit journal. Please try again. `,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      logError(error);
    } finally {
      // done
      setSaving(false);
    }
  };
  const handleAddEntry = () => {
    if (summary.isProcessed) return; // should never happen as the add button is disabled when processed, but just in case...
    const newIndex = entries.length;
    handlers.append({
      id: 0,
      productId: 0,
      productCode: "C",
      description: "",
      ordered: 0,
      supplied: 0,
      discount: selectedDebtor ? selectedDebtor.discount : null,
      ref: "",
      isBackOrderItem: false,
      unitPrice: null,
      eachPrice: null,
      cartonPrice: null,
      total: 0,
      gst: 0,
      gstUsed: getGstRateAsDecimal(),
      priceLevel: selectedDebtor?.sellPriceLevel ?? 1,
    });

    // Wait for Virtuoso to mount the new row
    setTimeout(() => {
      rowRefs.current[newIndex]?.focus();
    }, 0);
  };
  return (
    <Stack>
      <Title order={3}>
        Customer Order Entry
        {activeCustomer ? "" : " - Account Deactivated"}
      </Title>
      {Boolean(overDue) && (
        <CustomPageNotification title="Account Overdue!" message="" />
      )}
      {!activeCustomer && (
        <div
          style={{
            background: "#ffe0e0",
            padding: "1rem",
            borderRadius: "4px",
          }}
        >
          <Center>
            <Text>{`The selected debtor "${selectedDebtor?.name}" is marked as inactive.  Orders are not permitted for inactive debtors.`}</Text>
          </Center>
        </div>
      )}
      <Group grow align="flex-start">
        <TextInput
          label="Order ID"
          styles={{
            input: {
              backgroundColor: "#f8f9fa", // light grey (light mode)
              color: "#1a1a1a", // dark readable text
              cursor: "default",
            },
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          value={summary.id === 0 ? "New" : summary.id}
          readOnly
        />

        <DatePickerInput
          label="Order Date"
          value={orderDate}
          dropdownType="modal"
          readOnly={
            form.getValues().summary.isProcessed ||
            form.getValues().summary.id === 0
          } // only allow setting order date on new orders or unprocessed orders
          modalProps={{
            zIndex: 3000,
            size: "sm",
            withinPortal: true,
            trapFocus: false,
            centered: true,
            keepMounted: true,
          }}
          onChange={async (value: string | null) => {
            setOrderDate(value ?? null);
            if (value) {
              const dateOnly = value.split("T")[0]; // "2026-05-27"
              await changeOrderDate(Number(summary.id), dateOnly);
            }
          }}
          clearable
          w={250}
        />

        <Select
          label="Sales Person"
          placeholder="Select a sales person"
          clearable
          searchable
          data={salesPeople}
          {...form.getInputProps("summary.salesPerson")}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
        />
      </Group>
      <Group grow align="flex-start">
        <Autocomplete
          ref={invoiceToRef}
          clearable
          label="Invoice To (Debtor)"
          placeholder="Type name or ID"
          data={debtorOptions}
          filter={optionsFilter}
          value={invoiceToInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            invoiceToRef.current?.select();
          }}
          onChange={setInvoiceToInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const originalLabel = invoiceToInputValue;
            const match = debtorOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            form.setFieldValue("summary.invoiceToDebtorId", id);

            // get debtor
            const debtor = debtors.find((d) => d.id === id);
            form.setFieldValue("summary.salesPerson", debtor?.salesRep ?? "");
            setSelectedDebtor(debtor ?? null);
            setSystemNotes(debtor?.specialNotes ?? null);

            setActiveCustomer(debtor?.active ?? false);
            setOverdue(debtor?.overdue ?? false);

            setInvoiceToInputValue(match?.label ?? selected); // show full label in input

            // also default the deliver to to same as invoice to if not set
            //if (invoiceToInputValue !== deliverToInputValue) {
            form.setFieldValue("summary.deliverToDebtorId", id);
            setDeliverToInputValue(
              match?.label ?? selected /* use original input */,
            );
            //}
          }}
          readOnly={Number(summary.id) > 0}
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />

        <Autocomplete
          ref={deliverToRef}
          label="Deliver To (Debtor)"
          placeholder="Type name or ID"
          data={debtorOptions}
          clearable
          filter={optionsFilter}
          value={deliverToInputValue}
          comboboxProps={{
            withinPortal: true,
            zIndex: 3001,
          }}
          onFocus={() => {
            deliverToRef.current?.select();
          }}
          onChange={setDeliverToInputValue} // updates as user types
          onOptionSubmit={(selected) => {
            const match = debtorOptions.find(
              (d) => d.label === selected || d.value === selected,
            );
            const id = match ? Number(match.value) : 0;
            const debtor = debtors.find((d) => d.id === id);
            setSelectedDeliverToDebtor(debtor ?? null);
            setDeliverToInputValue(match?.label ?? selected); // show full label in input
          }}
          readOnly={Number(summary.id) > 0}
          error={debtorError ?? undefined}
          style={{ zIndex: 3000 }}
          styles={{ label: { fontWeight: 600 } }}
        />

        <TextInput
          label="Your Order #"
          placeholder="Enter customer order ref"
          styles={{
            label: { fontWeight: 600 },
          }}
          {...form.getInputProps("summary.yourOrderNo")}
        />
      </Group>
      <input type="hidden" {...form.getInputProps("summary.creditTo")} />
      <Group grow align="flex-start">
        <Textarea
          label="Notes 1"
          placeholder="Enter notes"
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          {...form.getInputProps("summary.notes")}
          mt="md"
        />
        <Textarea
          label="Notes 2"
          placeholder="Enter notes"
          styles={{
            label: {
              fontWeight: 600, // makes label bold
            },
          }}
          {...form.getInputProps("summary.notes2")}
          mt="md"
        />
      </Group>
      {/* running invoice total */}
      {form.getValues().summary.orderStage !== 0 && (
        <Group justify="flex-end" mt="md">
          <Stack gap={3}>
            <Group justify="flex-end">
              <Text size="md" fw={700}>
                Invoice Amount:
              </Text>
              <Text size="md" fw={700} c="dimmed">
                {formatNZD(total)}
              </Text>
            </Group>
            <Group justify="flex-end">
              <Text size="md" fw={700}>
                GST:
              </Text>
              <Text size="md" fw={700} c="dimmed">
                {formatNZD(gst)}
              </Text>
            </Group>
          </Stack>
        </Group>
      )}
      <Divider
        my="md"
        label={`Order Entries (Debtor Discounts: ${formatPercent(
          selectedDebtor?.discount,
          0,
          true,
        )})`}
        labelPosition="center"
      />
      <Title order={3}>Order Entries</Title>
      <Group justify="space-between" mt="md">
        <DefaultButtonAdd
          onClick={handleAddEntry}
          title={`${summary.id === 0 ? "You need to save order first to load back-orders" : ""} `}
          disabled={summary.id === 0 || summary.isProcessed}
          text=" Add New Item"
          variant="light"
        />
        <Group>
          <Button
            leftSection={
              <IconAlertCircle
                color={`${systemNotes ? "red" : "gray"}`}
                size={16}
              />
            }
            variant="outline"
            onClick={() => setShowSystemNotes(true)}
          >
            ATTENTION
          </Button>

          <Button
            onClick={() => handleSubmit()}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || !activeCustomer || summary.isProcessed}
          >
            Save
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            title="Save journal. This does not process the journal."
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={saving || !activeCustomer || summary.isProcessed}
          >
            Save & Close
          </Button>
          {Number(summary.id) > 0 && (
            <Button
              onClick={handlePrintPickingList}
              disabled={saving || !activeCustomer}
              leftSection={<IconPrinter size={16} />}
              rightSection={
                <IconCheck
                  color={summary.isPickingListPrinted ? "white" : "gray"}
                  size={16}
                />
              }
            >
              Picking List
            </Button>
          )}
          {Number(summary.id) > 0 && summary.isPickingListPrinted && (
            <>
              <Button
                onClick={handlePrintPackingSlip}
                disabled={saving || !activeCustomer}
                leftSection={<IconPrinter size={16} />}
                rightSection={
                  <IconCheck
                    color={
                      summary.isPackingSlipPrinted ||
                      summary.isPackingSlipPrinted
                        ? "white"
                        : "gray"
                    }
                    size={16}
                  />
                }
              >
                Packing Slip
              </Button>
              <Button
                onClick={handlePrintInvoice}
                disabled={
                  saving ||
                  summary.isPackingSlipPrinted === false ||
                  !activeCustomer
                }
                leftSection={<IconPrinter size={16} />}
              >
                Invoice
              </Button>
              <Button
                title="Process the order. This action cannot be undone."
                onClick={handleProcess}
                disabled={
                  summary.id === 0 ||
                  !activeCustomer ||
                  entries.length === 0 ||
                  saving ||
                  summary.isProcessed ||
                  summary.isInvoicePrinted === false ||
                  summary.isPackingSlipPrinted === false
                }
                leftSection={<IconProgressCheck size={16} />}
              >
                Process
              </Button>
            </>
          )}
        </Group>
      </Group>
      {error && (
        <Alert title="Error" color="red">
          {error}
        </Alert>
      )}
      {entries.length > 0 && (
        <Stack gap="sm" className="form">
          <Virtuoso
            style={{ height: 650, overflowY: "auto", contain: "layout" }}
            totalCount={entries.length}
            increaseViewportBy={{ top: 200, bottom: 200 }}
            overscan={300}
            defaultItemHeight={48} // ← stabilizes measurement
            components={{
              Header: () => (
                <OrderEntryHeaderRow orderStage={summary.orderStage} />
              ),
            }}
            itemContent={(index) => {
              const entry = entries[index];
              return (
                <OrderEntryRow
                  orderStage={form.getValues().summary.orderStage}
                  ref={(api) => {
                    rowRefs.current[index] = api;
                  }}
                  key={`${index}-${entry.id}-${summary.orderStage}`}
                  entry={entry}
                  index={index}
                  isLastRow={index === entries.length - 1}
                  onNewEntry={handleAddEntry}
                  debtorPriceLevel={selectedDebtor?.sellPriceLevel}
                  onUpdate={(updated, forceCalc) =>
                    handleUpdateEntry(index, updated, forceCalc)
                  }
                  onRemove={() => handleRemoveEntry(index)}
                />
              );
            }}
          />
        </Stack>
      )}
      <SavingModal opened={saving} onClose={() => setSaving(false)} />
      <Modal
        opened={showSystemNotes}
        onClose={() => setShowSystemNotes(false)}
        closeOnEscape={true}
        title={"Attention Required"}
        size="xl"
        centered
        withinPortal
        zIndex={9000}
      >
        <SafeHtml html={systemNotes ?? ""} />
      </Modal>
    </Stack>
  );
};

export default AdminOrderEntryPage;
